import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  AssetCategory,
  AssetDomain,
  FinancialItem,
  FinancialItemType,
  FinancialRating,
} from '../types';
import { useAppData } from '../context/AppDataContext';
import {
  ASSET_CATEGORY_OPTIONS,
  LIABILITY_CATEGORY_OPTIONS,
} from '../lib/assetCategories';
import { isAssetCategory, legacyCategoryToAssetCategory } from '../lib/assetRegistryNormalize';
import { GlobalButton } from './GlobalButton';
import { GlobalDatepicker } from './GlobalDatepicker';
import { GlobalDropdown, type GlobalDropdownOption } from './GlobalDropdown';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';

const NOTES_MAX_LENGTH = 2000;

type Props = {
  item?: FinancialItem | null;
  onSubmit: (next: FinancialItem) => void;
  onCancel: () => void;
  onDelete?: () => void;
};

const typeOptions: GlobalDropdownOption[] = [
  { value: 'asset', label: 'Asset' },
  { value: 'liability', label: 'Liability' },
];

const domainOptions: GlobalDropdownOption[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'business', label: 'Business' },
];

const ratingOptions: GlobalDropdownOption[] = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'bad', label: 'Bad' },
  { value: 'critical', label: 'Critical' },
];

function defaultCategory(type: FinancialItemType): AssetCategory {
  return type === 'liability' ? 'other_liability' : 'other_asset';
}

function normalizeCategoryForType(
  type: FinancialItemType,
  category: AssetCategory | string
): AssetCategory {
  if (isAssetCategory(category)) {
    const options = type === 'asset' ? ASSET_CATEGORY_OPTIONS : LIABILITY_CATEGORY_OPTIONS;
    if (options.some((option) => option.value === category)) return category;
    return defaultCategory(type);
  }
  if (typeof category === 'string' && category.trim()) {
    return legacyCategoryToAssetCategory(type, category);
  }
  return defaultCategory(type);
}

const empty = (): Omit<FinancialItem, 'id' | 'createdAt' | 'updatedAt'> => ({
  name: '',
  type: 'asset',
  domain: 'personal',
  category: 'other_asset',
  currentValue: 0,
  monthlyChange: 0,
  interestRate: undefined,
  rating: 'neutral',
  acquisitionDate: undefined,
  notes: undefined,
  minimumPayment: undefined,
  linkedAssetId: undefined,
  appreciationRate: undefined,
});

export function FinancialItemForm({ item, onSubmit, onCancel, onDelete }: Props) {
  const { financialItems } = useAppData();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [draft, setDraft] = useState(() => {
    if (!item) return empty();
    const type = item.type;
    return {
      name: item.name,
      type,
      domain: item.domain ?? 'personal',
      category: normalizeCategoryForType(type, item.category),
      currentValue: item.currentValue,
      monthlyChange: item.monthlyChange,
      interestRate: item.interestRate,
      rating: item.rating,
      acquisitionDate: item.acquisitionDate,
      notes: item.notes,
      minimumPayment: item.minimumPayment,
      linkedAssetId: item.linkedAssetId,
      appreciationRate: item.appreciationRate,
    };
  });

  useEffect(() => {
    setDeleteConfirmOpen(false);
    if (item) {
      const type = item.type;
      setDraft({
        name: item.name,
        type,
        domain: item.domain ?? 'personal',
        category: normalizeCategoryForType(type, item.category),
        currentValue: item.currentValue,
        monthlyChange: item.monthlyChange,
        interestRate: item.interestRate,
        rating: item.rating,
        acquisitionDate: item.acquisitionDate,
        notes: item.notes,
        minimumPayment: item.minimumPayment,
        linkedAssetId: item.linkedAssetId,
        appreciationRate: item.appreciationRate,
      });
    } else {
      setDraft(empty());
    }
  }, [item]);

  const categoryOptions =
    draft.type === 'asset' ? ASSET_CATEGORY_OPTIONS : LIABILITY_CATEGORY_OPTIONS;

  const linkedAssetOptions = useMemo(() => {
    const domain = draft.domain ?? 'personal';
    const options = financialItems
      .filter(
        (candidate) =>
          candidate.type === 'asset' &&
          (candidate.domain ?? 'personal') === domain &&
          candidate.id !== item?.id
      )
      .map((asset) => ({ value: asset.id, label: asset.name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (
      draft.linkedAssetId &&
      !options.some((option) => option.value === draft.linkedAssetId)
    ) {
      const linked = financialItems.find((candidate) => candidate.id === draft.linkedAssetId);
      options.unshift({
        value: draft.linkedAssetId,
        label: linked?.name ?? 'Linked asset (deleted or unavailable)',
      });
    }

    return options;
  }, [draft.domain, draft.linkedAssetId, financialItems, item?.id]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const domain = draft.domain;
    if (domain !== 'personal' && domain !== 'business') {
      return;
    }

    const now = new Date().toISOString();
    const base = item
      ? item
      : ({ id: '', createdAt: now, updatedAt: now } as FinancialItem);
    const interest =
      draft.interestRate === undefined || draft.interestRate === null
        ? undefined
        : Number(draft.interestRate);

    const acquisitionDate = draft.acquisitionDate?.trim() || undefined;
    const notesRaw = draft.notes?.trim() ?? '';
    const notes = notesRaw ? notesRaw.slice(0, NOTES_MAX_LENGTH) : undefined;

    const isLiability = draft.type === 'liability';
    const minimumPaymentRaw = draft.minimumPayment;
    const minimumPayment =
      isLiability &&
      minimumPaymentRaw !== undefined &&
      minimumPaymentRaw !== null &&
      Number.isFinite(Number(minimumPaymentRaw))
        ? Number(minimumPaymentRaw)
        : undefined;
    const linkedAssetId =
      isLiability && draft.linkedAssetId?.trim() ? draft.linkedAssetId.trim() : undefined;
    const appreciationRateRaw = draft.appreciationRate;
    const appreciationRate =
      !isLiability &&
      appreciationRateRaw !== undefined &&
      appreciationRateRaw !== null &&
      Number.isFinite(Number(appreciationRateRaw))
        ? Number(appreciationRateRaw)
        : undefined;

    onSubmit({
      ...base,
      ...draft,
      domain,
      interestRate: Number.isFinite(interest as number) ? (interest as number) : undefined,
      currentValue: Number(draft.currentValue) || 0,
      monthlyChange: Number(draft.monthlyChange) || 0,
      acquisitionDate,
      notes,
      minimumPayment,
      linkedAssetId,
      appreciationRate,
      updatedAt: now,
      createdAt: item?.createdAt ?? now,
      id: item?.id ?? base.id,
    });
  }

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      <GlobalInput
        label="Name"
        value={draft.name}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        required
      />
      <GlobalDropdown
        label="Domain"
        value={draft.domain ?? 'personal'}
        onChange={(v) => {
          const nextDomain = v as AssetDomain;
          const linkedStillValid =
            !draft.linkedAssetId ||
            financialItems.some(
              (candidate) =>
                candidate.id === draft.linkedAssetId &&
                candidate.type === 'asset' &&
                (candidate.domain ?? 'personal') === nextDomain
            );
          setDraft({
            ...draft,
            domain: nextDomain,
            linkedAssetId: linkedStillValid ? draft.linkedAssetId : undefined,
          });
        }}
        options={domainOptions}
      />
      <GlobalDropdown
        label="Type"
        value={draft.type}
        onChange={(v) => {
          const nextType = v as FinancialItemType;
          setDraft({
            ...draft,
            type: nextType,
            category: normalizeCategoryForType(nextType, draft.category),
            minimumPayment: nextType === 'liability' ? draft.minimumPayment : undefined,
            linkedAssetId: nextType === 'liability' ? draft.linkedAssetId : undefined,
            appreciationRate: nextType === 'asset' ? draft.appreciationRate : undefined,
          });
        }}
        options={typeOptions}
      />
      <GlobalDropdown
        label="Category"
        value={draft.category}
        onChange={(v) => setDraft({ ...draft, category: v as AssetCategory })}
        options={categoryOptions}
        required
      />
      <GlobalInput
        label="Current value (€)"
        type="number"
        step="any"
        value={draft.currentValue}
        onChange={(e) => setDraft({ ...draft, currentValue: Number(e.target.value) })}
      />
      <div>
        <GlobalInput
          label="Monthly change (€)"
          type="number"
          step="any"
          value={draft.monthlyChange}
          onChange={(e) => setDraft({ ...draft, monthlyChange: Number(e.target.value) })}
        />
        {draft.type === 'asset' ? (
          <p className="muted" style={{ marginTop: 6, fontSize: '0.75rem' }}>
            Manual drift if you don&apos;t model appreciation.
          </p>
        ) : null}
      </div>
      {draft.type === 'asset' ? (
        <GlobalInput
          label="Appreciation % p.a. (optional)"
          type="number"
          step="any"
          value={draft.appreciationRate ?? ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              appreciationRate: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      ) : null}
      <GlobalInput
        label="Interest rate % p.a. (optional)"
        type="number"
        step="any"
        value={draft.interestRate ?? ''}
        onChange={(e) =>
          setDraft({
            ...draft,
            interestRate: e.target.value === '' ? undefined : Number(e.target.value),
          })
        }
      />
      {draft.type === 'liability' ? (
        <>
          <GlobalInput
            label="Minimum monthly payment (€)"
            type="number"
            step="any"
            min="0"
            value={draft.minimumPayment ?? ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                minimumPayment: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
          <div>
            <GlobalDropdown
              label="Linked asset (optional)"
              value={draft.linkedAssetId ?? ''}
              onChange={(v) =>
                setDraft({
                  ...draft,
                  linkedAssetId: v === '' ? undefined : v,
                })
              }
              options={linkedAssetOptions}
              disabled={linkedAssetOptions.length === 0 && !draft.linkedAssetId}
            />
            <p className="muted" style={{ marginTop: 6, fontSize: '0.75rem' }}>
              Link a mortgage or loan to the property or asset it secures.
            </p>
          </div>
        </>
      ) : null}
      <GlobalDropdown
        label="Rating"
        value={draft.rating}
        onChange={(v) => setDraft({ ...draft, rating: v as FinancialRating })}
        options={ratingOptions}
      />
      <GlobalDatepicker
        label="Acquisition date (optional)"
        value={draft.acquisitionDate ?? ''}
        onChange={(e) =>
          setDraft({
            ...draft,
            acquisitionDate: e.target.value === '' ? undefined : e.target.value,
          })
        }
      />
      <div>
        <GlobalTextarea
          label="Notes (optional)"
          value={draft.notes ?? ''}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          rows={5}
          maxLength={NOTES_MAX_LENGTH}
          placeholder="Purchase context, valuation assumptions, lender details…"
        />
        <p className="muted" style={{ marginTop: 6, fontSize: '0.75rem', textAlign: 'right' }}>
          {(draft.notes ?? '').length.toLocaleString()} / {NOTES_MAX_LENGTH.toLocaleString()}
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'space-between',
          marginTop: 8,
        }}
      >
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {onDelete && !deleteConfirmOpen ? (
            <GlobalButton
              type="button"
              variant="secondary"
              destructive
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete
            </GlobalButton>
          ) : null}
          {onDelete && deleteConfirmOpen ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center',
              }}
            >
              <p className="muted" style={{ margin: 0, fontSize: '0.875rem' }}>
                Are you sure?
              </p>
              <GlobalButton
                type="button"
                variant="secondary"
                destructive
                onClick={() => {
                  onDelete();
                  setDeleteConfirmOpen(false);
                }}
              >
                Confirm delete
              </GlobalButton>
              <GlobalButton
                type="button"
                variant="secondary"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </GlobalButton>
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <GlobalButton type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </GlobalButton>
          <GlobalButton type="submit" variant="primary">
            Save
          </GlobalButton>
        </div>
      </div>
    </form>
  );
}
